import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Package, Play, Image } from "lucide-react";

// Helper to extract embed URL from various video platforms
function getEmbedUrl(url) {
  if (!url) return null;
  
  // YouTube
  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (ytMatch) {
    return { type: 'iframe', url: `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=0&controls=1` };
  }
  
  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return { type: 'iframe', url: `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=0` };
  }
  
  // ScreenPal (formerly Screencast-O-Matic)
  const screenpalMatch = url.match(/go\.screenpal\.com\/watch\/([a-zA-Z0-9]+)/);
  if (screenpalMatch) {
    return { type: 'iframe', url: `https://go.screenpal.com/player/${screenpalMatch[1]}` };
  }
  
  // Loom
  const loomMatch = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
  if (loomMatch) {
    return { type: 'iframe', url: `https://www.loom.com/embed/${loomMatch[1]}` };
  }
  
  // Google Drive
  const gdriveMatch = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (gdriveMatch) {
    return { type: 'iframe', url: `https://drive.google.com/file/d/${gdriveMatch[1]}/preview` };
  }
  
  // Direct video URL (MP4, WebM, etc.)
  if (url.match(/\.(mp4|webm|ogg)(\?|$)/i)) {
    return { type: 'video', url: url };
  }
  
  // Fallback - try as iframe embed
  return { type: 'iframe', url: url };
}

export default function ShowcaseProductModal({ product, open, onClose }) {
  const [activeMedia, setActiveMedia] = useState("image");
  
  if (!product) return null;

  const hasImage = !!product.image;
  const hasVideo = !!product.video;
  const embedData = hasVideo ? getEmbedUrl(product.video) : null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>{product.heading}</span>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Media Toggle (if both image and video exist) */}
          {hasImage && hasVideo && (
            <div className="flex gap-2 border-b pb-2">
              <Button
                variant={activeMedia === "image" ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveMedia("image")}
                className="gap-2"
              >
                <Image className="h-4 w-4" />
                Photo
              </Button>
              <Button
                variant={activeMedia === "video" ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveMedia("video")}
                className="gap-2"
              >
                <Play className="h-4 w-4" />
                Video
              </Button>
            </div>
          )}

          {/* Media Display */}
          <div className="relative h-64 md:h-80 overflow-hidden rounded-lg bg-muted">
            {((activeMedia === "video" && hasVideo) || (!hasImage && hasVideo)) && embedData ? (
              embedData.type === 'video' ? (
                <video 
                  src={embedData.url}
                  controls
                  className="w-full h-full object-contain"
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <iframe
                  src={embedData.url}
                  className="w-full h-full border-0"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={product.heading}
                />
              )
            ) : hasImage ? (
              <img 
                src={product.image} 
                alt={product.heading}
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <Package className="h-16 w-16 text-muted-foreground/50 mb-2" />
                <span className="text-muted-foreground">No Media Available</span>
              </div>
            )}
          </div>

          {/* SKU Badge */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-mono text-primary bg-primary/10 px-3 py-1 rounded">
              SKU: {product.sku}
            </span>
            {hasVideo && (
              <span className="text-xs bg-secondary/10 text-secondary px-2 py-1 rounded flex items-center gap-1">
                <Play className="h-3 w-3" fill="currentColor" />
                Video Available
              </span>
            )}
          </div>

          {/* Product Specifications */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-muted/30 rounded-lg border border-border/50">
              <div className="text-sm text-muted-foreground mb-1">Material</div>
              <div className="font-semibold">{product.material}</div>
            </div>
            <div className="p-4 bg-muted/30 rounded-lg border border-border/50">
              <div className="text-sm text-muted-foreground mb-1">Dimensions</div>
              <div className="font-semibold">{product.dimensions}</div>
            </div>
            <div className="p-4 bg-muted/30 rounded-lg border border-border/50">
              <div className="text-sm text-muted-foreground mb-1">Unit Weight</div>
              <div className="font-semibold">{product.unit_weight || "N/A"}</div>
            </div>
            <div className="p-4 bg-muted/30 rounded-lg border border-border/50">
              <div className="text-sm text-muted-foreground mb-1">Use / Application</div>
              <div className="font-semibold">{product.use}</div>
            </div>
          </div>

          {/* Product Description */}
          {product.description && (
            <div className="space-y-2">
              <h4 className="font-semibold text-lg" style={{ fontFamily: 'Manrope' }}>Product Description</h4>
              <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                {product.description}
              </p>
            </div>
          )}

          {/* CTA */}
          <div className="flex gap-3 pt-4 border-t">
            <Button className="flex-1" asChild>
              <a href={`mailto:sales@hokico.in?subject=Inquiry about ${product.sku}`}>
                Request Quote
              </a>
            </Button>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
